import type {
  Sd844EmailTemplate,
  Sd844TemplateColumnMapping,
  Sd844TemplateColumnSource,
  Sd844TemplateDraft,
  Sd844TemplateMappingSuggestion,
  Sd844TemplateSheetPreview,
} from '@erp/rpc';

/**
 * Editor state rules for the per-supplier 844 email template.
 *
 * Pure and component-free so the decisions that shape what a supplier is sent —
 * which file, which sheet, which bindings — can be tested without mounting a
 * form or stubbing a mutation.
 */

/** The editable shape of a template: everything except the file itself. */
export interface TemplateDraft {
  sheetName: string;
  dataStartRow: number;
  mappings: Sd844TemplateColumnMapping[];
  /** Other sheets of the workbook that are NOT sent to the supplier. */
  excludedSheetNames: string[];
}

/** A workbook dropped in the browser but not yet saved. */
export interface PendingTemplateUpload {
  filename: string;
  contentBase64: string;
  sheets: Sd844TemplateSheetPreview[];
}

/**
 * What the form shows: unsaved edits if there are any, otherwise the saved
 * template, otherwise nothing.
 *
 * Derived rather than copied into state by an effect — a sync effect would let
 * a background refetch overwrite bindings someone is midway through setting.
 */
export function deriveTemplateForm(
  draft: TemplateDraft | null,
  template: Sd844EmailTemplate | null,
): TemplateDraft | null {
  if (draft) return draft;
  if (!template) return null;
  return {
    sheetName: template.sheetName,
    dataStartRow: template.dataStartRow,
    mappings: template.columnMappings,
    excludedSheetNames: template.excludedSheetNames,
  };
}

/**
 * Seed bindings from a sheet's detected columns.
 *
 * Every binding starts blank here; `applySuggestions` fills in what the
 * matcher proposed once it returns. Kept separate so dropping a file renders
 * its columns immediately rather than waiting on a model round-trip.
 */
export function draftFromSheet(
  sheet: Sd844TemplateSheetPreview,
  allSheets: Sd844TemplateSheetPreview[] = [sheet],
): TemplateDraft {
  return {
    sheetName: sheet.name,
    dataStartRow: sheet.suggestedDataStartRow,
    mappings: sheet.columns.map((column) => ({
      columnIndex: column.columnIndex,
      headerLabel: column.headerLabel,
      ...(column.hint ? { hint: column.hint } : {}),
      source: { kind: 'blank' as const },
      // Until the matcher (or a person) resolves this column, this draft is
      // intentionally ineligible for activation.
      needsReview: true,
    })),
    excludedSheetNames: suggestedExclusions(sheet.name, allSheets),
  };
}

/**
 * Sheets proposed for removal: any OTHER sheet that is our own default 844
 * layout — the "Example" tab the S&D team pastes in while mapping. It carries a
 * real claim line, so it must not go to the supplier. Only a proposal; the
 * editor shows a checkbox per sheet.
 */
export function suggestedExclusions(
  claimSheet: string,
  allSheets: Sd844TemplateSheetPreview[],
): string[] {
  const names: string[] = [];
  for (const sheet of allSheets) {
    if (sheet.name !== claimSheet && sheet.looksLikeDefaultLayout) {
      names.push(sheet.name);
    }
  }
  return names;
}

/** The draft with one column rebound; every other column untouched. */
export function withColumnSource(
  draft: TemplateDraft,
  columnIndex: number,
  source: Sd844TemplateColumnSource,
): TemplateDraft {
  return {
    ...draft,
    // Picking a value — even the same one — is the human's confirmation, so
    // the review flag goes.
    mappings: draft.mappings.map((mapping) =>
      mapping.columnIndex === columnIndex
        ? { ...withoutReviewFlag(mapping), source }
        : mapping,
    ),
  };
}

function withoutReviewFlag(
  mapping: Sd844TemplateColumnMapping,
): Sd844TemplateColumnMapping {
  const { needsReview, ...rest } = mapping;
  void needsReview;
  return rest;
}

/** Toggle one sheet in or out of the file the supplier receives. */
export function toggleExcludedSheet(
  draft: TemplateDraft,
  sheetName: string,
  excluded: boolean,
): TemplateDraft {
  const others = draft.excludedSheetNames.filter((n) => n !== sheetName);
  return {
    ...draft,
    excludedSheetNames:
      excluded && sheetName !== draft.sheetName
        ? [...others, sheetName]
        : others,
  };
}

/**
 * The binding a suggestion proposes, or `null` when it proposes nothing.
 *
 * A suggestion carries either a full `source` (a fixed value from the notes,
 * or a deliberate blank) or just a `field`. Both shapes exist because the field
 * form predates the notes row.
 */
export function suggestedSource(
  suggestion: Sd844TemplateMappingSuggestion,
): Sd844TemplateColumnSource | null {
  if (suggestion.source) return suggestion.source;
  if (!suggestion.field) return null;
  return {
    kind: 'field',
    field: suggestion.field,
    ...(suggestion.dateFormat ? { dateFormat: suggestion.dateFormat } : {}),
  };
}

/**
 * Fold proposed bindings into a draft.
 *
 * Only fills columns still blank, so a suggestion arriving late can never
 * overwrite something the user already picked. A suggestion of `null` — the
 * matcher could not place the column — deliberately leaves it blank; the editor
 * says so rather than letting it look like a deliberate choice.
 */
export function applySuggestions(
  draft: TemplateDraft,
  suggestions: Sd844TemplateMappingSuggestion[],
): TemplateDraft {
  const byColumn = new Map(suggestions.map((s) => [s.columnIndex, s]));

  return {
    ...draft,
    mappings: draft.mappings.map((mapping) => {
      if (mapping.source.kind !== 'blank') return mapping;
      const suggestion = byColumn.get(mapping.columnIndex);
      const source = suggestion ? suggestedSource(suggestion) : null;
      if (!source) return { ...mapping, needsReview: true };
      // Anything short of one of our own labels is flagged for a human.
      return suggestion?.confidence === 'likely'
        ? { ...mapping, source, needsReview: true }
        : { ...withoutReviewFlag(mapping), source };
    }),
  };
}

/** Column indexes the matcher could not place — shown as "couldn't determine". */
export function unresolvedColumns(
  suggestions: Sd844TemplateMappingSuggestion[],
): Set<number> {
  return new Set(
    suggestions
      .filter((s) => suggestedSource(s) === null)
      .map((s) => s.columnIndex),
  );
}

/** Why each column got its proposal — shown under the binding for review. */
export function suggestionReasons(
  suggestions: Sd844TemplateMappingSuggestion[],
): Map<number, string> {
  return new Map(
    suggestions
      .filter((s): s is typeof s & { reason: string } => !!s.reason)
      .map((s) => [s.columnIndex, s.reason]),
  );
}

/**
 * What the editor should MARK, derived from what was actually applied.
 *
 * Deliberately computed by comparing the draft before and after, not read off
 * the raw suggestions: a user editing while matching is in flight keeps their
 * pick (`applySuggestions` skips non-blank columns), and reading the raw list
 * would still badge that column as machine-matched — or restore "couldn't
 * determine" over a column the person just resolved themselves.
 */
export function deriveMatchingStatus(
  before: TemplateDraft,
  after: TemplateDraft,
  suggestions: Sd844TemplateMappingSuggestion[],
): { suggested: Set<number>; unresolved: Set<number> } {
  const afterByColumn = new Map(
    after.mappings.map((m) => [m.columnIndex, m.source]),
  );

  const suggested = new Set<number>();
  for (const mapping of before.mappings) {
    const next = afterByColumn.get(mapping.columnIndex);
    if (mapping.source.kind === 'blank' && next && next.kind !== 'blank') {
      suggested.add(mapping.columnIndex);
    }
  }

  const couldNotPlace = unresolvedColumns(suggestions);
  const unresolved = new Set<number>();
  for (const mapping of after.mappings) {
    if (
      mapping.source.kind === 'blank' &&
      couldNotPlace.has(mapping.columnIndex)
    ) {
      unresolved.add(mapping.columnIndex);
    }
  }

  return { suggested, unresolved };
}

/**
 * Columns flagged "Check this match": their binding came from a match that is
 * not certain, and nobody has picked a value for them since. Read from the
 * mappings themselves, so the flag survives a save and a reload.
 */
export function reviewColumns(draft: TemplateDraft): Set<number> {
  const columns = new Set<number>();
  for (const mapping of draft.mappings) {
    if (mapping.needsReview) columns.add(mapping.columnIndex);
  }
  return columns;
}

export interface SaveTemplateInput {
  /** `null` = the organization default. */
  companyId: string | null;
  upload?: { filename: string; contentBase64: string };
  sheetName: string;
  dataStartRow: number;
  columnMappings: Sd844TemplateColumnMapping[];
  excludedSheetNames: string[];
}

/**
 * Build the save payload.
 *
 * `upload` is omitted when nothing new was dropped, which is what tells the
 * server to keep the stored workbook and re-save only the bindings — the
 * difference between correcting a mapping and replacing a supplier's form.
 */
export function buildSaveTemplateInput({
  companyId,
  form,
  pendingUpload,
}: {
  companyId: string | null;
  form: TemplateDraft;
  pendingUpload: PendingTemplateUpload | null;
}): SaveTemplateInput {
  return {
    companyId,
    ...(pendingUpload
      ? {
          upload: {
            filename: pendingUpload.filename,
            contentBase64: pendingUpload.contentBase64,
          },
        }
      : {}),
    sheetName: form.sheetName,
    dataStartRow: form.dataStartRow,
    columnMappings: form.mappings,
    excludedSheetNames: form.excludedSheetNames,
  };
}

/**
 * The draft a test render carries — the form exactly as on screen, so the
 * person sees the file their unsaved bindings would produce. `upload` is
 * included only for a freshly-dropped file; otherwise the server renders the
 * stored workbook.
 */
export function buildTestDraft(
  form: TemplateDraft,
  pendingUpload: PendingTemplateUpload | null,
): Sd844TemplateDraft {
  return {
    ...(pendingUpload
      ? {
          upload: {
            filename: pendingUpload.filename,
            contentBase64: pendingUpload.contentBase64,
          },
        }
      : {}),
    sheetName: form.sheetName,
    dataStartRow: form.dataStartRow,
    columnMappings: form.mappings,
    excludedSheetNames: form.excludedSheetNames,
  };
}

/** Group order in the field dropdown: our claim file first, then the rest. */
const FIELD_GROUP_ORDER = { claim: 0, transaction: 1, vdm: 2 } as const;

/**
 * The field dropdown's options: grouped (claim / transaction / VDM), then A–Z
 * by label within each group, so a field is found by reading, not scrolling a
 * schema-ordered list.
 */
export function sortFieldOptions<
  T extends { label: string; group: keyof typeof FIELD_GROUP_ORDER },
>(options: T[]): T[] {
  return [...options].sort(
    (a, b) =>
      FIELD_GROUP_ORDER[a.group] - FIELD_GROUP_ORDER[b.group] ||
      a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }),
  );
}

/**
 * The sheet names offered for exclusion. A fresh upload knows them all; a
 * saved template recorded them at upload (`sheetNames`). Templates saved before
 * that column existed know only what they already exclude — still listed, so
 * an exclusion can always be undone.
 */
export function exclusionCandidates(
  pendingUpload: PendingTemplateUpload | null,
  template: Sd844EmailTemplate | null,
  excludedSheetNames: string[],
): string[] {
  if (pendingUpload) return pendingUpload.sheets.map((s) => s.name);
  return [...new Set([...(template?.sheetNames ?? []), ...excludedSheetNames])];
}
